// ============================================================
// Disconnection Alert - 斷線警示系統 Interfaces
// ============================================================

// AlertScopeType enum - 對應 Prisma schema
export enum AlertScopeType {
  system = 'system',
  ad_supplier = 'ad_supplier',
  provider = 'provider',
}

// ============================================================
// Setting - 設定
// ============================================================

// 基本設定介面
export interface DisconnectionAlertSetting {
  alert_setting_id: number
  scope_type: AlertScopeType
  scope_id: number | null
  is_enabled: boolean
  offline_threshold_minutes: number
  email_subject: string | null
  email_template: string | null
  created_at: Date
  updated_at: Date
}

// 設定含關聯資料
export interface DisconnectionAlertSettingWithRelations extends DisconnectionAlertSetting {
  schedules?: DisconnectionAlertSchedule[]
  recipients?: DisconnectionAlertRecipient[]
  active_windows?: DisconnectionAlertActiveWindow[]
}

// 建立/更新設定的請求
export interface UpsertDisconnectionAlertSettingRequest {
  scope_type: AlertScopeType
  scope_id?: number | null
  is_enabled?: boolean
  offline_threshold_minutes?: number
  email_subject?: string | null
  email_template?: string | null
}

// ============================================================
// Schedule - 檢查時間排程
// ============================================================

// 基本排程介面
export interface DisconnectionAlertSchedule {
  schedule_id: number
  alert_setting_id: number
  check_time: string // 格式 "HH:mm"，需為 15 分鐘倍數
  is_enabled: boolean
  created_at: Date
}

// 建立排程的請求
export interface CreateDisconnectionAlertScheduleRequest {
  alert_setting_id: number
  check_time: string // 格式 "HH:mm"，需為 15 分鐘倍數 (00, 15, 30, 45)
  is_enabled?: boolean
}

// 更新排程的請求
export interface UpdateDisconnectionAlertScheduleRequest {
  check_time?: string
  is_enabled?: boolean
}

// ============================================================
// Recipient - 通知收件人
// ============================================================

// 基本收件人介面
export interface DisconnectionAlertRecipient {
  recipient_id: number
  alert_setting_id: number
  name: string
  email: string
  is_enabled: boolean
  created_at: Date
}

// 建立收件人的請求
export interface CreateDisconnectionAlertRecipientRequest {
  alert_setting_id: number
  name: string
  email: string
  is_enabled?: boolean
}

// 更新收件人的請求
export interface UpdateDisconnectionAlertRecipientRequest {
  name?: string
  email?: string
  is_enabled?: boolean
}

// ============================================================
// Active Window - 工作時段
// ============================================================

// 基本工作時段介面
export interface DisconnectionAlertActiveWindow {
  window_id: number
  alert_setting_id: number
  start_time: string // 格式 "HH:mm"，需為 15 分鐘倍數
  end_time: string // 格式 "HH:mm"，需為 15 分鐘倍數
  is_enabled: boolean
  created_at: Date
}

// 建立工作時段的請求
export interface CreateDisconnectionAlertActiveWindowRequest {
  alert_setting_id: number
  start_time: string // 格式 "HH:mm"，需為 15 分鐘倍數 (00, 15, 30, 45)
  end_time: string // 格式 "HH:mm"，需為 15 分鐘倍數 (00, 15, 30, 45)
  is_enabled?: boolean
}

// 更新工作時段的請求
export interface UpdateDisconnectionAlertActiveWindowRequest {
  start_time?: string
  end_time?: string
  is_enabled?: boolean
}

// ============================================================
// Log - 發送紀錄
// ============================================================

// 離線診所資訊
export interface OfflineClinicInfo {
  clinic_id: number
  clinic_name: string
  devices: {
    device_id: number
    name: string
    last_connected_at: string | null
  }[]
}

// 通知結果資訊
export interface RecipientNotificationResult {
  name: string
  email: string
  success: boolean
  error?: string
}

// 基本紀錄介面
export interface DisconnectionAlertLog {
  log_id: number
  alert_setting_id: number
  check_time: string
  checked_at: Date
  offline_clinic_count: number
  offline_clinics: OfflineClinicInfo[]
  recipients_notified: RecipientNotificationResult[]
  execution_time_ms: number | null
}

// 查詢紀錄的請求參數
export interface GetDisconnectionAlertLogsQuery {
  alert_setting_id?: number
  scope_type?: AlertScopeType
  scope_id?: number
  start_date?: string // 格式 "YYYY-MM-DD"
  end_date?: string // 格式 "YYYY-MM-DD"
  limit?: number
  offset?: number
}

// ============================================================
// Manual Trigger - 手動觸發
// ============================================================

// 手動觸發請求
export interface TriggerDisconnectionAlertRequest {
  alert_setting_id: number
}

// 手動觸發結果
export interface TriggerDisconnectionAlertResult {
  setting_id: number
  checked_at: Date
  offline_clinic_count: number
  offline_clinics: OfflineClinicInfo[]
  notifications_sent: number
  notifications_failed: number
}

// ============================================================
// Cron - 排程檢查
// ============================================================

// 排程檢查結果
export interface CronCheckResult {
  success: boolean
  processed: number
  skipped: number
  lockAcquired: boolean
  error?: string
}

// ============================================================
// API Responses
// ============================================================

// 通用錯誤格式
export interface DisconnectionAlertError {
  code: string
  message: string
}

// 設定 Response
export interface DisconnectionAlertSettingResponse {
  success: boolean
  data?: DisconnectionAlertSettingWithRelations | null
  error?: DisconnectionAlertError | string
}

// 排程 Response
export interface DisconnectionAlertScheduleResponse {
  success: boolean
  data?: DisconnectionAlertSchedule
  error?: DisconnectionAlertError | string
}

// 工作時段 Response
export interface DisconnectionAlertActiveWindowResponse {
  success: boolean
  data?: DisconnectionAlertActiveWindow
  error?: DisconnectionAlertError | string
}

// 收件人 Response
export interface DisconnectionAlertRecipientResponse {
  success: boolean
  data?: DisconnectionAlertRecipient
  error?: DisconnectionAlertError | string
}

// 紀錄列表 Response
export interface DisconnectionAlertLogsResponse {
  success: boolean
  data?: DisconnectionAlertLog[]
  total?: number
  error?: DisconnectionAlertError | string
}

// 手動觸發 Response
export interface TriggerDisconnectionAlertResponse {
  success: boolean
  data?: TriggerDisconnectionAlertResult
  error?: DisconnectionAlertError | string
}

// 刪除操作 Response
export interface DisconnectionAlertDeleteResponse {
  success: boolean
  error?: DisconnectionAlertError | string
}
